import { useState, useEffect, useCallback } from "react";
import { Plus, Sparkles, Pencil, Trash2, X, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

const ICON_OPTIONS = ["Sparkles", "Shield", "Heart", "HeartHandshake", "Flame", "Sun", "Feather", "Compass", "Star", "BookOpen", "Brain", "Leaf"];
const COVER_PRESETS = [
  "photo-1619694770795-e21c58464159", "photo-1593351799227-75df2026356b",
  "photo-1741253323551-8d774db9d956", "photo-1600144559281-53d1dee79a99",
  "photo-1517840933437-c41356892b35", "photo-1632765866070-3fadf25d3d5b",
].map((id) => `https://images.unsplash.com/${id}?crop=entropy&cs=srgb&fm=jpg&q=80&w=800`);

const blank = () => ({ title: "", subtitle: "", description: "", icon: "Sparkles", color: "#7B628B", image_url: "", collection: "", collection_title: "", lessons: [] });

// Stable client-only key so lesson rows keep their input state across add/remove.
const lessonKey = () => globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
const withKey = (l) => ({ ...l, _k: l._k || lessonKey() });

const COLLECTION_SUGGESTIONS = [
  "Women 40+", "Young Adult Women", "Caregivers", "Women of Faith", "Women in Leadership",
  "Women Starting Over", "Military Women & Spouses", "First-Generation Women", "Rural Women",
  "Women Navigating Class Mobility",
];

export function CourseManager({ open, onClose, onSaved }) {
  const [courses, setCourses] = useState([]);
  const [form, setForm] = useState(null); // null = list view; object = editing
  const [editId, setEditId] = useState(null);
  const [topic, setTopic] = useState("");
  const [genLoading, setGenLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/courses"); setCourses(data.courses || []); }
    catch { toast.error("Couldn't load your courses."); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLesson = (i, k, v) => setForm((f) => ({ ...f, lessons: f.lessons.map((l, j) => (j === i ? { ...l, [k]: v } : l)) }));
  const addLesson = () => setForm((f) => ({ ...f, lessons: [...f.lessons, { _k: lessonKey(), title: "", summary: "", minutes: 5, sections: [], takeaways: [], reflection: "", practice: "" }] }));
  const removeLesson = (i) => setForm((f) => ({ ...f, lessons: f.lessons.filter((_, j) => j !== i) }));

  const startCreate = () => { setEditId(null); setForm(blank()); };
  const startEdit = (c) => { setEditId(c.id); setForm({ title: c.title, subtitle: c.subtitle || "", description: c.description || "", icon: c.icon || "Sparkles", color: c.color || "#7B628B", image_url: c.image_url || "", collection: c.collection || "", collection_title: c.collection_title || "", lessons: (c.lessons || []).map(withKey) }); };

  const generate = async () => {
    if (!topic.trim()) return toast.error("Enter a topic first.");
    setGenLoading(true);
    try {
      const { data } = await api.post("/admin/courses/generate", { topic, num_lessons: 4 });
      setEditId(null);
      setForm({ title: data.title || "", subtitle: data.subtitle || "", description: data.description || "", icon: "Sparkles", color: "#7B628B", image_url: "", collection: "", collection_title: "", lessons: (data.lessons || []).map(withKey) });
      toast.success("Draft ready — review and save.");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI generation failed."); }
    finally { setGenLoading(false); }
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Course title is required.");
    setSaving(true);
    const payload = { ...form, lessons: form.lessons.map(({ _k, ...rest }) => rest) };
    try {
      if (editId) await api.put(`/admin/courses/${editId}`, payload);
      else await api.post("/admin/courses", payload);
      toast.success(editId ? "Course updated" : "Course created");
      setForm(null); setEditId(null); await load(); onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete "${c.title}" and its lessons?`)) return;
    await api.delete(`/admin/courses/${c.id}`);
    toast.success("Deleted"); await load(); onSaved && onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="course-manager" className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Manage courses</DialogTitle>
        </DialogHeader>

        {!form ? (
          <div className="space-y-5">
            <div className="rounded-2xl bg-[#F7F2EA] p-4 space-y-3">
              <Label className="flex items-center gap-1.5 text-[#291B2D]"><Sparkles size={14} className="text-[#C89B55]" /> Draft with AI</Label>
              <div className="flex gap-2">
                <Input data-testid="ai-topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Setting boundaries with family" />
                <Button onClick={generate} disabled={genLoading} data-testid="ai-generate-btn" className="bg-[#291B2D] text-white shrink-0">
                  {genLoading ? <Loader2 size={16} className="animate-spin" /> : "Generate"}
                </Button>
              </div>
            </div>
            <Button onClick={startCreate} data-testid="course-create-btn" variant="outline" className="w-full"><Plus size={16} className="mr-1" /> Create manually</Button>
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-widest text-[#291B2D]/50 font-semibold">Your courses ({courses.length})</p>
              {courses.length === 0 && <p className="text-sm text-[#291B2D]/50">No custom courses yet.</p>}
              {courses.map((c) => (
                <div key={c.id} data-testid={`owner-course-${c.id}`} className="flex items-center gap-3 bg-white border border-[#291B2D]/8 rounded-2xl p-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-heading text-base truncate">{c.title}</p>
                    <p className="text-xs text-[#291B2D]/50">{c.lessons?.length || 0} lessons</p>
                  </div>
                  <button onClick={() => startEdit(c)} data-testid={`edit-course-${c.id}`} className="w-8 h-8 rounded-full bg-[#F7F2EA] flex items-center justify-center"><Pencil size={14} /></button>
                  <button onClick={() => del(c)} data-testid={`delete-course-${c.id}`} className="w-8 h-8 rounded-full bg-[#FBEAEA] text-[#9B2C2C] flex items-center justify-center"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Title</Label><Input data-testid="course-title" value={form.title} onChange={(e) => set("title", e.target.value)} /></div>
              <div className="col-span-2"><Label>Subtitle</Label><Input value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} placeholder="Short tagline" /></div>
              <div className="col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
              <div className="col-span-2 rounded-xl bg-[#F7F2EA] border border-[#E5DED4] p-3">
                <Label>Lived-experience collection <span className="font-normal text-[#8A838E]">(optional — groups this course under "Explore what shapes us")</span></Label>
                <Input data-testid="course-collection" list="collection-suggestions" value={form.collection_title}
                  onChange={(e) => { const v = e.target.value; set("collection_title", v); set("collection", v.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")); }}
                  placeholder="e.g. Women 40+, Caregivers, Women of Faith — leave blank for a universal course" className="mt-1" />
                <datalist id="collection-suggestions">
                  {COLLECTION_SUGGESTIONS.map((s) => <option key={s} value={s} />)}
                </datalist>
              </div>
              <div><Label>Icon</Label>
                <Select value={form.icon} onValueChange={(v) => set("icon", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{ICON_OPTIONS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Accent color</Label><Input type="color" value={form.color} onChange={(e) => set("color", e.target.value)} className="h-10 p-1" /></div>
            </div>

            <div>
              <Label>Cover image URL</Label>
              <Input data-testid="course-image" value={form.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="https://…  (paste any image link)" />
              <div className="flex gap-2 mt-2 flex-wrap">
                {COVER_PRESETS.map((u) => (
                  <button key={u} type="button" onClick={() => set("image_url", u)}
                    className={`w-14 h-10 rounded-lg overflow-hidden border-2 ${form.image_url === u ? "border-[#C89B55]" : "border-transparent"}`}>
                    <img src={u} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Lessons ({form.lessons.length})</Label>
                <button onClick={addLesson} data-testid="add-lesson" className="text-sm text-[#3D6B52] font-semibold inline-flex items-center gap-1"><Plus size={14} /> Add lesson</button>
              </div>
              <div className="space-y-2">
                {form.lessons.map((l, i) => (
                  <div key={l._k} className="bg-[#F7F2EA] rounded-xl p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <Input value={l.title} onChange={(e) => setLesson(i, "title", e.target.value)} placeholder={`Lesson ${i + 1} title`} className="bg-white" data-testid={`lesson-title-${i}`} />
                      <Input type="number" value={l.minutes} onChange={(e) => setLesson(i, "minutes", parseInt(e.target.value) || 1)} className="w-16 bg-white" />
                      <button onClick={() => removeLesson(i)} className="text-[#9B2C2C] shrink-0"><X size={16} /></button>
                    </div>
                    <Input value={l.summary} onChange={(e) => setLesson(i, "summary", e.target.value)} placeholder="One-line summary" className="bg-white" />
                    {l.sections?.length > 0 && <p className="text-[11px] text-[#291B2D]/45">{l.sections.length} AI sections · {l.takeaways?.length || 0} takeaways included</p>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {form ? (
            <>
              <Button variant="ghost" onClick={() => { setForm(null); setEditId(null); }}>Back</Button>
              <Button onClick={save} disabled={saving} data-testid="course-save" className="bg-[#291B2D] text-white">{saving ? "Saving…" : editId ? "Update" : "Create"}</Button>
            </>
          ) : (
            <Button variant="ghost" onClick={onClose}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
